import { create } from 'zustand';
import { User } from '../types';
import api, { extractApiError } from '../services/api';

const USER_KEY = 'auth_user';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  currentOrgSlug: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (targetOrgSlug: string) => Promise<void>;
  refreshToken: () => Promise<string | null>;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  accessToken: null,
  currentOrgSlug: localStorage.getItem('currentOrgSlug'),
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.auth.login(email, password);
      const defaultOrg = data.user.defaultOrgSlug || '';
      localStorage.setItem('currentOrgSlug', defaultOrg);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({
        accessToken: null,
        user: data.user,
        currentOrgSlug: defaultOrg,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (err: unknown) {
      const message = extractApiError(err, 'Login failed. Please check your details and try again.');
      set({ isLoading: false, error: message, isAuthenticated: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.auth.logout();
    } catch {
      // Ignore errors on logout — always clear local state
    } finally {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('currentOrgSlug');
      set({ user: null, accessToken: null, currentOrgSlug: null, isAuthenticated: false, error: null });
    }
  },

  switchOrg: async (targetOrgSlug: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.auth.switchOrg(targetOrgSlug);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.setItem('currentOrgSlug', targetOrgSlug);
      set({
        accessToken: null,
        user: data.user,
        currentOrgSlug: targetOrgSlug,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (err: unknown) {
      const message = extractApiError(err, 'Failed to switch organization. Please try again.');
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  refreshToken: async (): Promise<string | null> => {
    try {
      await api.auth.refresh();
      return null;
    } catch {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('currentOrgSlug');
      set({ user: null, accessToken: null, currentOrgSlug: null, isAuthenticated: false });
      return null;
    }
  },

  loadUser: async () => {
    const storedUserStr = localStorage.getItem(USER_KEY);
    if (!storedUserStr) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    set({ isLoading: true });
    try {
      const user = await api.auth.me();
      const orgSlug = localStorage.getItem('currentOrgSlug');
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user, currentOrgSlug: orgSlug, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('currentOrgSlug');
      set({ user: null, accessToken: null, currentOrgSlug: null, isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
